'use client';

import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

const DEMO_CREDENTIALS = [
  { role: 'Super Admin / CEO', email: 'demo.admin@mihr-test.local', password: 'DemoAdmin@9999', color: 'bg-violet-500' },
  { role: 'Operations Manager', email: 'demo.ops@mihr-test.local', password: 'DemoOps@9999', color: 'bg-blue-500' },
  { role: 'HR Manager', email: 'demo.hr@mihr-test.local', password: 'DemoHr@9999', color: 'bg-emerald-500' },
  { role: 'Accounts', email: 'demo.accounts@mihr-test.local', password: 'DemoAccounts@9999', color: 'bg-amber-500' },
  { role: 'Employer', email: 'demo.employer@mihr-test.local', password: 'DemoEmployer@9999', color: 'bg-orange-500' },
  { role: 'Agent', email: 'demo.agent@mihr-test.local', password: 'DemoAgent@9999', color: 'bg-cyan-500' },
  { role: 'Candidate', email: 'demo.candidate@mihr-test.local', password: 'DemoCandidate@9999', color: 'bg-pink-500' },
];

export default function LoginPage() {
  const { signIn } = useAuth();
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDemo, setShowDemo] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signIn(email, password);
      router.push('/');
    } catch (err: any) {
      setError(err.message || 'Invalid email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = (cred: (typeof DEMO_CREDENTIALS)[0]) => {
    setEmail(cred.email);
    setPassword(cred.password);
    setShowDemo(false);
    setError('');
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left panel — branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary/90 to-primary flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 rounded-full bg-white/20 blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
        </div>
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-12">
            <AppLogo size={40} />
            <span className="text-white text-2xl font-700 tracking-tight">MIHR</span>
          </div>
          <h1 className="text-4xl font-700 text-white leading-tight mb-4">
            International Recruitment<br />Operating System
          </h1>
          <p className="text-white/70 text-lg leading-relaxed">
            Manage the complete recruitment lifecycle — from employer vacancies through candidate deployment — in one centralized platform.
          </p>
        </div>
        <div className="relative z-10 grid grid-cols-2 gap-4">
          {[
            { label: 'Active Candidates', value: '2,847' },
            { label: 'Open Vacancies', value: '143' },
            { label: 'Employers', value: '38' },
            { label: 'Deployed This Year', value: '412' },
          ].map((stat) => (
            <div key={stat.label} className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
              <p className="text-white text-2xl font-700">{stat.value}</p>
              <p className="text-white/60 text-sm mt-0.5">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Right panel — login form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <AppLogo size={32} />
            <span className="text-foreground text-xl font-700">MIHR</span>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-700 text-foreground">Welcome back</h2>
            <p className="text-muted-foreground mt-1 text-sm">Sign in to your MIHR account</p>
          </div>

          {error && (
            <div className="mb-4 flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
              <Icon name="ExclamationCircleIcon" size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-500 text-foreground mb-1.5">Email address</label>
              <div className="relative">
                <Icon name="EnvelopeIcon" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="you@mihr.com"
                  className="w-full pl-10 pr-4 py-2.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all placeholder:text-muted-foreground"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-500 text-foreground mb-1.5">Password</label>
              <div className="relative">
                <Icon name="LockClosedIcon" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="w-full pl-10 pr-10 py-2.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all placeholder:text-muted-foreground"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-primary-foreground py-2.5 rounded-lg text-sm font-600 hover:bg-primary/90 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </form>

          {/* Demo credentials */}
          <div className="mt-6">
            <button
              onClick={() => setShowDemo(!showDemo)}
              className="w-full flex items-center justify-between px-4 py-3 bg-muted rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-all"
            >
              <span className="font-500">Demo Credentials (7 Roles)</span>
              <Icon name={showDemo ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={14} />
            </button>

            {showDemo && (
              <div className="mt-2 border border-border rounded-lg overflow-hidden divide-y divide-border">
                {DEMO_CREDENTIALS.map((cred) => (
                  <button
                    key={cred.email}
                    onClick={() => fillDemo(cred)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-muted transition-colors text-left"
                  >
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${cred.color}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-600 text-foreground">{cred.role}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{cred.email}</p>
                    </div>
                    <span className="text-[10px] text-muted-foreground font-mono bg-muted px-1.5 py-0.5 rounded">
                      {cred.password}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <p className="mt-6 text-center text-xs text-muted-foreground">
            Need an account?{' '}
            <a href="/register" className="text-primary font-500 hover:underline">
              Register here
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
